import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { Article } from '@/lib/content';
import { fetchArticle } from '@/lib/content';
import { parseMarkdown } from '@/lib/markdown';
import { formatDate } from '@/lib/format';
import { ButtonLink } from '@/components/Button';
import { Icon } from '@/components/Icon';
import { Prose } from '@/components/Prose';
import { Section } from '@/components/Section';
import { Seo } from '@/components/Seo';

/** 技术文章详情。正文是后台维护的 Markdown，经轻量解析器转成结构化块。 */
export function ArticlePage() {
  const { slug = '' } = useParams();
  const [article, setArticle] = useState<Article | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'missing'>('loading');

  useEffect(() => {
    const controller = new AbortController();
    setStatus('loading');
    fetchArticle(slug, controller.signal)
      .then((value) => {
        setArticle(value);
        setStatus('ready');
      })
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        setStatus('missing');
      });
    return () => controller.abort();
  }, [slug]);

  if (status === 'loading') {
    return (
      <Section spacing="loose">
        <p className="text-sm text-fg-muted">正在加载文章…</p>
      </Section>
    );
  }

  if (status === 'missing' || !article) {
    return (
      <>
        <Seo path="/articles" title="文章未找到 — Havorix™" description="这篇文章不存在或尚未发布。" />
        <Section spacing="loose">
          <div className="mx-auto flex max-w-xl flex-col items-center gap-4 text-center">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-grad-tint text-brand">
              <Icon name="alert" size={22} />
            </span>
            <h1 className="text-2xl font-semibold text-fg">未找到该文章</h1>
            <p className="text-sm leading-relaxed text-fg-muted">
              链接可能有误，或该文章尚未发布。技术文章页列有当前全部可读内容。
            </p>
            <ButtonLink to="/articles" variant="primary">
              回到技术文章
            </ButtonLink>
          </div>
        </Section>
      </>
    );
  }

  return (
    <>
      <Seo
        path={`/articles/${article.slug}`}
        title={`${article.title} — Havorix™`}
        description={article.excerpt}
        type="article"
        publishedAt={article.publishedAt ?? undefined}
        tags={article.tags}
        crumbs={[
          { name: '首页', path: '/' },
          { name: '技术文章', path: '/articles' },
          { name: article.title, path: `/articles/${article.slug}` },
        ]}
      />

      <Section tone="grid" spacing="tight" className="border-b border-line">
        <div className="max-w-3xl">
          <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-fg-muted">
            <Link to="/articles" className="transition-colors hover:text-fg">
              技术文章
            </Link>
            <Icon name="arrowRight" size={12} className="text-fg-subtle" />
            <span className="metric-value">
              {article.publishedAt ? formatDate(article.publishedAt) : '未注明日期'}
            </span>
          </nav>

          <h1 className="mt-4 text-3xl font-semibold leading-tight text-fg md:text-[2.25rem] md:leading-[1.2]">
            {article.title}
          </h1>

          <p className="mt-4 text-base leading-relaxed text-fg-muted">{article.excerpt}</p>

          <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-fg-subtle">
            <span>Havorix 工程团队</span>
            <span aria-hidden="true">·</span>
            <span>约 {article.minutes} 分钟</span>
            {article.tags.length > 0 ? (
              <>
                <span aria-hidden="true">·</span>
                <span>{article.tags.join(' / ')}</span>
              </>
            ) : null}
          </div>
        </div>
      </Section>

      <Section spacing="loose">
        <div className="mx-auto max-w-3xl">
          <Prose blocks={parseMarkdown(article.body)} />

          <div className="mt-12 border-t border-line pt-6">
            <Link to="/articles" className="inline-flex items-center gap-1.5 text-sm text-fg-muted hover:text-fg">
              <Icon name="arrowLeft" size={14} />
              返回技术文章列表
            </Link>
          </div>
        </div>
      </Section>
    </>
  );
}
